import { IconButton, makeStyles } from '@material-ui/core';
import { ThumbUp } from '@material-ui/icons';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import cn from 'classnames';
import { Fragment } from 'react';
import { COLORS } from '../../theme/variables';
import Text from './text';

interface AssignmentRecommendedTableProps {
  labels: [string, string];
  rows: Array<{
    name: string;
    assigned?: string | number;
    interested?: boolean;
    onAdd: () => any;
    ai?: boolean;
  }>;
  mobileFullHeight?: boolean;
}

const useStyles = makeStyles({
  table: {
    display: 'grid',
    columnGap: 20,
    alignItems: 'center',
    maxHeight: 175,
    overflowY: 'scroll',
    overflowX: 'hidden',
    marginTop: 10,
    width: '100%',
    minWidth: 0,

    '@media (max-width: 700px)': {
      columnGap: 10,
    },
  },
  table1: { gridTemplateColumns: '40px 1fr' },
  table2: { gridTemplateColumns: '40px minmax(0, 1fr) auto' },
  tHead: {
    display: 'contents',
    height: '100%',
    '& > *': {
      position: 'sticky',
      top: 0,
      background: '#fff',
      zIndex: 1,
    },
  },
  tbody: {
    display: 'contents',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    '& > span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
  },
  empty: {
    alignSelf: 'stretch',
  },
  mobileFullHeight: {
    '@media (max-width: 700px)': {
      maxHeight: 'none',
    },
  },
  assignedCell: {
    textAlign: 'right',
    paddingRight: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  ai: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '5px 5.1px 5px 4.1px',
    color: COLORS.COLOR_ADDITIONAL_WHITE,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.4,
    background: COLORS.COLOR_ADDITIONAL_DODGER_BLUE,
    borderRadius: 4,
  },
  likedIconWrapper: {
    width: 22,
    height: 22,
    borderRadius: '100%',
    backgroundColor: COLORS.COLOR_BLUE_BASE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    opacity: 0.7,
  },
  likedIcon: {
    width: 14,
    height: 14,
    cursor: 'pointer',
    transform: 'rotateY(180deg)',
  },
  icons: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    height: 'max-content',
    flexShrink: 0,
  },
});

function AssignmentRecommendedTable({
  labels,
  rows,
  mobileFullHeight = false,
}: AssignmentRecommendedTableProps) {
  const classes = useStyles();
  const showAssigned = rows.some((row) => row.assigned !== undefined);
  const showInterested = rows.some((row) => row.interested);

  return (
    <div
      className={cn({
        [classes.table]: true,
        [classes.table1]: !showAssigned,
        [classes.table2]: showAssigned,
        [classes.mobileFullHeight]: mobileFullHeight,
      })}>
      <div className={classes.tHead}>
        <div className={classes.empty} />
        <Text variant='normal' bold>
          {labels[0]}
        </Text>
        {showAssigned && (
          <Text variant='normal' bold className={classes.assignedCell}>
            {labels[1]}
          </Text>
        )}
      </div>
      <div className={classes.tbody}>
        {rows.map((row, index) => (
          <Fragment key={index}>
            <IconButton onClick={row.onAdd} data-testid={`add-${index}`}>
              <AddCircleIcon />
            </IconButton>
            <div className={classes.nameCell}>
              <Text variant='normal' title={row.name}>
                {row.name}
              </Text>
              <span className={classes.icons}>
                {row.ai ? <div className={classes.ai}>AI</div> : null}
                {showInterested && row.interested ? (
                  <span className={classes.likedIconWrapper}>
                    <ThumbUp className={classes.likedIcon} />
                  </span>
                ) : null}
              </span>
            </div>
            {showAssigned && (
              <Text variant='normal' className={classes.assignedCell}>
                {row.assigned}
              </Text>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default AssignmentRecommendedTable;
